import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { TrendingDown, Heart, Quote, Loader2 } from "lucide-react";
import { API } from "./coach/SharedComponents";

/**
 * RetentionInsightsCard — admin-only glanceable dashboard card.
 * Pattern-matches the top 3 exit reasons ("why they leave") and the top 3
 * stay reasons ("why they stay"), plus surfaces recent free-text quotes.
 *
 * Hormozi: review these every time you hit 20+ cancels; act on the top 1-2.
 */
export const RetentionInsightsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("30d"); // 30d | 90d | all

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/feedback/admin/retention-insights`, {
          credentials: "include",
        });
        if (r.ok) setData(await r.json());
      } catch (e) {
        console.error("retention insights fetch failed", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="bg-[#141B2D] border border-[#1E293B] rounded-2xl md:rounded-3xl p-5 md:p-6 h-64 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const exitReasons =
    range === "30d" ? data.exit.top_reasons_30d :
    range === "90d" ? data.exit.top_reasons_90d :
    data.exit.top_reasons_all;
  const exitCount =
    range === "30d" ? data.exit.count_30d :
    range === "90d" ? data.exit.count_90d :
    data.exit.count_all;
  const stayReasons = data.stay.top_tags_all;
  const maxExitCount = Math.max(1, ...exitReasons.map((r) => r.count));
  const maxStayCount = Math.max(1, ...stayReasons.map((r) => r.count));

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#141B2D] border border-[#1E293B] rounded-2xl md:rounded-3xl p-5 md:p-6"
      data-testid="retention-insights-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg md:text-xl font-bold text-white">Retention Insights</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Pattern-match churn drivers · Celebrate stay drivers
          </p>
        </div>
        <div className="flex gap-1 bg-[#0B0F1A] rounded-lg p-1" data-testid="insights-range-toggle">
          {["30d", "90d", "all"].map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
                range === r
                  ? "bg-[#1E293B] text-white"
                  : "text-gray-500 hover:text-gray-300"
              }`}
              data-testid={`insights-range-${r}`}
            >
              {r === "all" ? "All" : r}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* ===== WHY THEY LEAVE ===== */}
        <div data-testid="insights-exit-column">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-red-500/15 flex items-center justify-center">
              <TrendingDown className="w-3.5 h-3.5 text-red-400" />
            </div>
            <p className="text-xs uppercase tracking-wide text-red-400 font-bold">
              Why they leave
            </p>
            <span className="ml-auto text-xs text-gray-500">
              {exitCount} cancel{exitCount === 1 ? "" : "s"}
            </span>
          </div>

          {exitReasons.length === 0 ? (
            <div className="py-6 text-center bg-[#0B0F1A] border border-[#1E293B] rounded-xl">
              <p className="text-sm text-gray-500">No cancellations in this period. Nice.</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {exitReasons.map((r) => {
                const pct = (r.count / maxExitCount) * 100;
                return (
                  <div
                    key={r.key}
                    className="bg-[#0B0F1A] border border-[#1E293B] rounded-lg p-3"
                    data-testid={`exit-reason-row-${r.key}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm text-white font-medium">{r.label}</span>
                      <span className="text-xs font-bold text-red-300">{r.count}</span>
                    </div>
                    <div className="h-1.5 bg-[#1E293B] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-red-500/80 to-red-400/80 rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {data.exit.recent_quotes.length > 0 && (
            <div className="mt-4">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">
                Recent exit feedback
              </p>
              <div className="space-y-2">
                {data.exit.recent_quotes.slice(0, 2).map((q, i) => (
                  <blockquote
                    key={i}
                    className="text-xs text-gray-300 italic bg-[#0B0F1A] border-l-2 border-red-500/50 pl-3 py-1.5 pr-2"
                  >
                    <Quote className="w-3 h-3 inline-block text-red-500/70 mr-1 -mt-0.5" />
                    {q.text}
                  </blockquote>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ===== WHY THEY STAY ===== */}
        <div data-testid="insights-stay-column">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-emerald-500/15 flex items-center justify-center">
              <Heart className="w-3.5 h-3.5 text-emerald-400" />
            </div>
            <p className="text-xs uppercase tracking-wide text-emerald-400 font-bold">
              Why they stay
            </p>
            <span className="ml-auto text-xs text-gray-500">
              {data.stay.count_all} response{data.stay.count_all === 1 ? "" : "s"}
            </span>
          </div>

          {stayReasons.length === 0 ? (
            <div className="py-6 text-center bg-[#0B0F1A] border border-[#1E293B] rounded-xl">
              <p className="text-sm text-gray-500">
                Stay responses will appear after athletes hit day 30.
              </p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {stayReasons.map((r) => {
                const pct = (r.count / maxStayCount) * 100;
                return (
                  <div
                    key={r.key}
                    className="bg-[#0B0F1A] border border-[#1E293B] rounded-lg p-3"
                    data-testid={`stay-reason-row-${r.key}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm text-white font-medium">{r.label}</span>
                      <span className="text-xs font-bold text-emerald-300">{r.count}</span>
                    </div>
                    <div className="h-1.5 bg-[#1E293B] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500/80 to-emerald-400/80 rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {data.stay.recent_quotes.length > 0 && (
            <div className="mt-4">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">
                Athletes say
              </p>
              <div className="space-y-2">
                {data.stay.recent_quotes.slice(0, 2).map((q, i) => (
                  <blockquote
                    key={i}
                    className="text-xs text-gray-300 italic bg-[#0B0F1A] border-l-2 border-emerald-500/50 pl-3 py-1.5 pr-2"
                  >
                    <Quote className="w-3 h-3 inline-block text-emerald-500/70 mr-1 -mt-0.5" />
                    {q.text}
                  </blockquote>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default RetentionInsightsCard;
